import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { checkUserExist } from '../../graphql/contacts/check-user-exist';
import { createContact } from '../../graphql/contacts/create-contact';
import { createJobRole } from '../../graphql/job-roles/create-job-role';
import { deleteJobRole } from '../../graphql/job-roles/delete-job-role';
import { getJobRoles } from '../../graphql/job-roles/get-job-roles';
import { getContact } from '../../graphql/contacts/get-contact';
import { getContactByEmail } from '../../graphql/contacts/get-contact-by-email';
import { getContactHistories } from '../../graphql/contacts/get-contact-histories';
import { getContacts } from '../../graphql/contacts/get-contacts';
import { importUser } from '../../graphql/contacts/import-user';
import { queryForContacts } from '../../graphql/contacts/query-for-contacts';
import { queryForJobRoles } from '../../graphql/contacts/query-for-job-roles';
import { queryForWhoReportsToMe } from '../../graphql/contacts/query-for-who-reports-to-me';
import { updateContact } from '../../graphql/contacts/update-contact';
import { updateJobRole } from '../../graphql/job-roles/update-job-role';
import { BaseAppSyncService } from '../base-app-sync.service';
import { allowJobRoleDelete } from '../../graphql/job-roles/allow-job-role-delete';
import { queryForContactDropdown } from '../../graphql/contacts/query-for-contact-dropdown';
import { JobRoleDataTableInput } from '../../model/admin/job-role-data-table-input';
import { getTurnoverContacts } from '../../graphql/contacts/get-turnover-contacts';
import { markConsentGivenAt } from '../../graphql/contacts/mark-consent-given-at';
import { getFullContactByEmail } from '../../graphql/contacts/get-full-contact-by-email';
import {
  Contact,
  ContactConnection,
  ContactSortOrQuery,
  ContactDropdown,
  ContactDropdownConnection,
} from '../../model/contacts/contact';
import { ContactBuilderService } from '../builder/contact-builder.service';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import { Audit } from 'src/app/model/common/audit';
import { ContactUpdateDto } from 'src/app/model/contacts/contact-update-dto';
import { ContactExistResult } from 'src/app/model/contacts/contact-exist-result';
import {
  JobRole,
  JobRoleConnection,
  JobRoleSortOrQuery,
} from 'src/app/model/admin/job-role';
import { markUserInactive } from 'src/app/graphql/contacts/mark-user-inactive';

@Injectable({
  providedIn: 'root',
})
export class ContactApiService extends BaseAppSyncService {
  private _contactBuilder = inject(ContactBuilderService);

  getJobRoles(): Observable<JobRole[]> {
    return this.query(
      getJobRoles,
      {},
      (items: any[]) => items.map((i) => this._contactBuilder.buildJobRole(i)),
      'getJobRoles'
    );
  }

  getContacts(): Observable<(Contact | null)[]> {
    return this.query(
      getContacts,
      {},
      (items: any[]) => items.map((i) => this._contactBuilder.buildContact(i)),
      'getContacts',
      'cache-first'
    );
  }

  getContactHistories(id: string): Observable<Audit[]> {
    return this.query(
      getContactHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getContactHistories'
    );
  }

  updateContact(id: string, contactInput: any): Observable<Contact | null> {
    return this.mutate(
      updateContact,
      { id: id, input: contactInput },
      (i: any) => this._contactBuilder.buildContact(i),
      'updateContact'
    );
  }

  queryForWhoReportsToMe(email: string): Observable<ContactDropdown[]> {
    return this.query(
      queryForWhoReportsToMe,
      { email },
      (i: any[]) => i,
      'queryForWhoReportsToMe'
    );
  }

  queryForContacts(
    limit: number,
    page: number,
    query: ContactSortOrQuery,
    sort: ContactSortOrQuery
  ): Observable<ContactConnection> {
    return this.query(
      queryForContacts,
      { limit, page, query, sort },
      (i: any) => this._contactBuilder.buildContactConnection(i),
      'queryForContacts'
    );
  }

  queryForContactDropdown(
    limit: number,
    page: number,
    query: ContactSortOrQuery,
    sort: ContactSortOrQuery
  ): Observable<ContactDropdownConnection> {
    return this.query(
      queryForContactDropdown,
      { limit, page, query, sort },
      (i: any) => this._contactBuilder.buildContactDropdownConnection(i),
      'queryForContactDropdown'
    );
  }

  checkUserExist(): Observable<ContactExistResult> {
    return this.query(
      checkUserExist,
      {},
      (i: any) => this._contactBuilder.buildContactExistResult(i),
      'checkUserExist'
    );
  }

  importUser(): Observable<Contact | null> {
    return this.mutate(
      importUser,
      {},
      (i: any) => this._contactBuilder.buildContact(i),
      'importUser'
    );
  }

  getContact(id: string): Observable<Contact | null> {
    return this.query(
      getContact,
      { id },
      (i: any) => this._contactBuilder.buildContact(i),
      'getContact'
    );
  }

  getContactByEmail(email: string): Observable<Contact> {
    return this.query(
      getContactByEmail,
      { email },
      (item: any) => item as Contact,
      'getContactByEmail'
    );
  }

  getFullContactByEmail(email: string): Observable<Contact | null> {
    return this.query(
      getFullContactByEmail,
      { email },
      (i: any) => this._contactBuilder.buildContact(i),
      'getFullContactByEmail'
    );
  }

  getTurnoverContacts(id: string): Observable<ContactDropdown[]> {
    return this.query(
      getTurnoverContacts,
      { id },
      (i: any[]) => i.map((i) => this._contactBuilder.buildContactDropdown(i)),
      'getTurnoverContacts'
    );
  }

  createContact(contactInput: ContactUpdateDto): Observable<Contact | null> {
    return this.mutate(
      createContact,
      { input: contactInput },
      (i: any) => this._contactBuilder.buildContact(i),
      'createContact'
    );
  }

  queryForJobRoles(
    limit: number,
    page: number,
    query: JobRoleSortOrQuery,
    sort: JobRoleSortOrQuery
  ): Observable<JobRoleConnection> {
    return this.query(
      queryForJobRoles,
      { limit, page, query, sort },
      (i: any) => this._contactBuilder.buildJobRoleConnection(i),
      'queryForJobRoles'
    );
  }

  createJobRole(input: JobRoleDataTableInput | null): Observable<JobRole> {
    return this.mutate(
      createJobRole,
      { input },
      (i: any) => this._contactBuilder.buildJobRole(i),
      'createJobRole'
    );
  }

  updateJobRole(
    id: string,
    changes: JobRoleDataTableInput | null
  ): Observable<JobRole> {
    return this.mutate(
      updateJobRole,
      { id, changes },
      (i: any) => this._contactBuilder.buildJobRole(i),
      'updateJobRole'
    );
  }

  deleteJobRole(id: string): Observable<boolean> {
    return this.mutate(
      deleteJobRole,
      { id },
      (i: boolean) => i,
      'deleteJobRole'
    );
  }

  allowJobRoleDelete(id: string): Observable<boolean> {
    return this.query(
      allowJobRoleDelete,
      { id },
      (i: any) => i.value,
      'allowJobRoleDelete'
    );
  }

  markConsentGivenAt(id: string): Observable<null> {
    return this.mutate(
      markConsentGivenAt,
      { id },
      () => null,
      'markConsentGivenAt'
    );
  }

  markUserInactive(id: string): Observable<null> {
    return this.mutate(
      markUserInactive,
      { id },
      () => null,
      'markUserInactive'
    );
  }
}
